import React, { useState, useEffect, useRef } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import './FAQ.css';
import api from '../../api/axios';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
const [deleteItem, setDeleteItem] = useState(null);

  const [page, setPage] = useState(1);
const [limit] = useState(10);
const [totalPages, setTotalPages] = useState(1);
const [statusFilter, setStatusFilter] = useState('active');
const [open, setOpen] = useState(false);
const dropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    status: 'active'
  });

const fetchFaqs = async () => {
  try {
    const res = await api.get('/admin/questions', {
      params: {
        page,
        limit,
        search: debouncedSearch,
        status: statusFilter,
      },
    });

    setFaqs(res.data?.data || []);
    setTotalPages(res.data?.pagination?.totalPages || 1);
  } catch (err) {
    console.error(err);
    alert('Failed to fetch FAQs');
  }
};


  useEffect(() => {
    fetchFaqs()
  }, [page, debouncedSearch, statusFilter])



  useEffect(() => {
  if (showDeleteModal) {
    document.body.classList.add("modal-active");
    document.activeElement?.blur();
  } else {
    document.body.classList.remove("modal-active");
  }

  return () => document.body.classList.remove("modal-active");
}, [showDeleteModal]);



  useEffect(() => {
  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setOpen(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);


  const handleSubmit = async(e) => {
    e.preventDefault();
     try {
      const body = {
        question: formData?.question,
        answer: formData?.answer,
        ...(editingFaq && { status: formData?.status }) // include status only if updating
      };

      if (editingFaq) {
        // Update existing FAQ
        await api.put(`/admin/questions/${editingFaq._id}`, body);
        toast.success('FAQ updated successfully');
      } else {
        // Create new FAQ
        await api.post('/admin/questions', body);
        toast.success('FAQ created successfully');
      }

      // Reset modal & form
      setShowForm(false);
      setEditingFaq(null);
      setFormData({ question: '', answer: '', status: 'active' });

      // Refresh FAQ list
      fetchFaqs();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Something went wrong!');
    }
  };

  const handleEdit = (faq) => {
    setFormData(faq);
    setEditingFaq(faq);
    setShowForm(true);
  };


const handleDelete = (faq) => {
    document.activeElement?.blur();
  setDeleteItem(faq);
  setShowDeleteModal(true);
}



  return (
    <div  className={`faq-container ${
    showDeleteModal || showForm ? "bg-disabled" : ""
  }`} >

      <div className="faq-header">
        <h1>FAQ Management</h1>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          Add New FAQ
        </button>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Search questions..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
        />


<div className="custom-select" ref={dropdownRef}>
  <div
    className="selected"
    onClick={() => setOpen(!open)}
  >
    <span>{statusFilter || "All"}</span>

    <span className={`arrow ${open ? "open" : ""}`}>
      ▾
    </span>
  </div>

  {open && (
    <ul className="options">
      <li onClick={() => { setStatusFilter(""); setPage(1); setOpen(false); }}>
        All
      </li>
      <li onClick={() => { setStatusFilter("active"); setPage(1); setOpen(false); }}>
        Active
      </li>
      <li onClick={() => { setStatusFilter("inactive"); setPage(1); setOpen(false); }}>
        Inactive
      </li>
    </ul>
  )}
</div>



      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingFaq ? 'Edit FAQ' : 'Add New FAQ'}</h2>
              <button className="close-btn" onClick={() => {
                setShowForm(false);
                setEditingFaq(null);
                setFormData({ question: '', answer: '', status: 'active' });
              }}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="faq-form">
              <div className="form-group">
                <label>Question *</label>
                <input
                  type="text"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Answer *</label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  rows="4"
                  required
                />
              </div>
              {editingFaq && (
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingFaq ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowForm(false);
                  setEditingFaq(null);
                  setFormData({ question: '', answer: '', status: 'active' });
                }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="faq-list">
        {faqs.map((faq) => (
          <div key={faq._id} className="faq-item">
            <div className="faq-content">
              <div className="faq-question">
                <h3>{faq.question}</h3>
                <span className={`status-badge ${faq.status}`}>{faq.status}</span>
              </div>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
            <div className="faq-actions">
              <button className="btn-edit" onClick={() => handleEdit(faq)}>
                Edit
              </button>
              <button className="btn-delete" onClick={() => handleDelete(faq._id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

       <div className="pagination">
  <button
    className="page-btn"
    disabled={page <= 1}
    onClick={() => setPage(page - 1)}
  >
    Prev
  </button>

  <span className="page-info">
    Page {page} of {totalPages}
  </span>

  <button
    className="page-btn"
    disabled={page >= totalPages}
    onClick={() => setPage(page + 1)}
  >
    Next
  </button>
</div>


      {faqs.length === 0 && (
        <div className="no-data">
          <p>No FAQs found</p>
        </div>
      )}
       <ToastContainer
              toastClassName="custom-toast"
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
            {showDeleteModal && deleteItem && (
  <div className="modal-overlay" onClick={(e) => {
    if (e.target.classList.contains("modal-overlay")) {
      setShowDeleteModal(false);
      setDeleteItem(null);
    }
  }}>
    <div className="modal delete-modal">
      <h3>Delete Confirmation</h3>
      <p>
        Are you sure you want to delete
        <strong> {deleteItem.question}</strong>?
      </p>
      <div className="delete-modal-footer">
        <button className="btn-cancel" onClick={() => {setShowDeleteModal(false); setDeleteItem(null)}}>Cancel</button>
        <button className="btn-deletee" onClick={async () => {
          try {
            await api.delete(`/admin/questions/${deleteItem}`);
            toast.success('FAQ deleted successfully');
            fetchFaqs();
          } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || 'Failed to delete FAQ');
          } finally {
            setShowDeleteModal(false);
            setDeleteItem(null);
          }
        }}>Delete</button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default FAQ;